import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faLeftRight } from "@fortawesome/free-solid-svg-icons";
import "./TheGuessinator.css";
import TipButton from "../../../../TipButton";
import StudentInfoTooltip from "../../../../StudentInfoTooltip";

const TheGuessinator = React.memo(
  ({
    question,
    gameWord = "",
    onChange,
    isStudentView = false,
    border,
    tip,
    onFlip,
  }) => {
    const [word, setWord] = useState(gameWord.toUpperCase());
    const [guessedLetters, setGuessedLetters] = useState(new Set());
    const [remainingAttempts, setRemainingAttempts] = useState(6);
    const [gameStatus, setGameStatus] = useState("initial");
    const [losingStarIndex, setLosingStarIndex] = useState(null);
    const containerRef = useRef(null);
    const gameStateRef = useRef({
      gameStatus: "initial",
      guessedLetters: new Set(),
      word: "",
      remainingAttempts: 6,
    });

    const isGameReady = !isStudentView ? question.trim() && word.trim() : true;

    useEffect(() => {
      setWord(gameWord.toUpperCase());
    }, [gameWord]);

    useEffect(() => {
      gameStateRef.current = {
        gameStatus,
        guessedLetters,
        word,
        remainingAttempts,
      };
    }, [gameStatus, guessedLetters, word, remainingAttempts]);

    const startGame = () => {
      setGameStatus("playing");
      setGuessedLetters(new Set());
      setRemainingAttempts(6);
      // Automatically focus the container so keyboard input works immediately
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.focus();
        }
      }, 0);
    };

    const handleKeyPress = (e) => {
      if (gameStateRef.current.gameStatus !== "playing") return;

      if (e.code === "Space") {
        e.preventDefault();
      }

      const letter = e.key.toUpperCase();
      if (
        /^[A-Z]$/.test(letter) &&
        !gameStateRef.current.guessedLetters.has(letter)
      ) {
        const newGuessedLetters = new Set(gameStateRef.current.guessedLetters);
        newGuessedLetters.add(letter);
        setGuessedLetters(newGuessedLetters);

        if (!gameStateRef.current.word.includes(letter)) {
          setLosingStarIndex(gameStateRef.current.remainingAttempts - 1);
          setTimeout(() => {
            setLosingStarIndex(null);
            if (gameStateRef.current.remainingAttempts <= 1) {
              setRemainingAttempts(0);
              setGameStatus("lost");
            } else {
              setRemainingAttempts((prev) => prev - 1);
            }
          }, 500);
        }

        const isWon = gameStateRef.current.word
          .split("")
          .every(
            (char) =>
              newGuessedLetters.has(char) || char === " " || char === "-"
          );

        let newGameStatus = gameStateRef.current.gameStatus;
        if (isWon) {
          newGameStatus = "won";
          setGameStatus("won");
        }

        // Send response to parent component
        if (isStudentView) {
          onChange({
            guessedLetters: Array.from(newGuessedLetters),
            gameStatus: newGameStatus,
            remainingAttempts: Math.max(
              0,
              gameStateRef.current.remainingAttempts - 1
            ),
          });
        }
      }
    };

    useEffect(() => {
      const handleFocus = () => {
        window.addEventListener("keypress", handleKeyPress);
      };

      const handleBlur = () => {
        window.removeEventListener("keypress", handleKeyPress);
      };

      const container = containerRef.current;
      container.addEventListener("focus", handleFocus);
      container.addEventListener("blur", handleBlur);

      return () => {
        container.removeEventListener("focus", handleFocus);
        container.removeEventListener("blur", handleBlur);
      };
    }, []);

    const renderWord = () => {
      return (
        <div className="word-display">
          {word.split("").map((letter, index) =>
            letter === " " ? (
              <span
                key={index}
                style={{ width: "20px", display: "inline-block" }}
              ></span>
            ) : (
              <div
                key={index}
                className={`letter-block ${
                  letter === "-"
                    ? "dash"
                    : guessedLetters.has(letter)
                    ? "filled"
                    : "empty"
                }`}
              >
                {letter === "-"
                  ? "-"
                  : guessedLetters.has(letter)
                  ? letter
                  : ""}
              </div>
            )
          )}
        </div>
      );
    };

    const handleWordChange = (e) => {
      const newWord = e.target.value.toUpperCase();
      setWord(newWord);
      onChange({ gameWord: newWord });
    };

    return (
      <div
        className={`question-container ${border}`}
        tabIndex={0}
        ref={containerRef}
      >
        <div className="question-header game-header">
          <input
            type="text"
            value={question}
            onChange={(e) => onChange({ question: e.target.value })}
            placeholder={
              isStudentView ? "Guess the word!" : "Enter your question here"
            }
            readOnly={isStudentView}
            className={`question-input ${isStudentView ? "student-view" : ""}`}
          />
          {!isStudentView && (
            <input
              type="text"
              className="question-input"
              value={word}
              onChange={handleWordChange}
              placeholder="Enter the word to guess"
            />
          )}
        </div>

        <div className="the-guessinator-game">
          {!isGameReady ? (
            <div className="game-setup-message">
              Please enter both a question and a word to guess
            </div>
          ) : gameStatus === "initial" ? (
            <div className="game-container">
              <h2>Ready to play?</h2>
              <button className="play-button" onClick={startGame}>
                PLAY
              </button>
            </div>
          ) : (
            <>
              <div className="game-status">
                <div className="attempts-remaining">
                  {[...Array(remainingAttempts)].map((_, i) => (
                    <FontAwesomeIcon
                      key={i}
                      icon={faStar}
                      className={`star-icon ${
                        i === losingStarIndex ? "losing" : ""
                      }`}
                    />
                  ))}
                </div>
                {renderWord()}
              </div>
              {gameStatus === "playing" && (
                <div className="input-hint">
                  Type any letter on your keyboard to guess
                </div>
              )}
              {gameStatus !== "playing" && gameStatus !== "initial" && (
                <div className={`game-result ${gameStatus}`}>
                  <p>
                    {gameStatus === "won"
                      ? "Congratulations! You won!"
                      : "Game Over!"}
                  </p>
                  <button className="reset-button" onClick={startGame}>
                    Play Again
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="question-footer">
          <div className="question-footer-flex">
            <div className="button-wrapper"></div>
            <TipButton
              tip={tip}
              onChange={onChange}
              isStudentView={isStudentView}
              onFlip={onFlip}
            />
            <div className="scorebox-container"></div>
          </div>
        </div>
      </div>
    );
  }
);

export default TheGuessinator;
